/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*
* Factories for the service-request status and assistant pills (see FormPillComponent / PillField). The
* state lists come straight off the model (ServiceRequest.srStates / srSubStates carry the chip
* colour and tooltip); this file only wires the click flows.
*/
import { UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import { MyInjector } from '@src/app/app.module';
import { AbstractObject } from '@src/app/model/abstract-object';
import { ServiceRequest } from '@src/app/model/serviceRequest';
import { Router } from '@angular/router';
import { AI_OUTCOMES, AiLog } from '@src/app/model/ai-log';
import { AiProposalResult, RequestService } from '@src/app/modules/crm/request.service';
import { AiLogPageNavRoute } from '@src/app/routes/ai-log-page-nav';
import { Field } from '@src/app/shared/field/Field';
import { FormPillComponent, PillField, PillState } from '@src/app/shared/form/form-pill/form-pill.component';
import { ServiceRequestPageComponent } from '@src/app/modules/crm/service-request-page/service-request-page.component';
import { ActionGridComponent } from '@src/app/shared/action/action-grid.component';
import { ActionListDialogComponent } from '@src/app/shared/action/action-list-dialog.component';

interface PillOpts {
    /** Extra refresh after a form-mode click - grid rows and table rows refresh themselves. */
    afterFormChange?: (sr: ServiceRequest) => void;
}

const dialog = () => MyInjector.instance.get(MatDialog);
const requests = () => MyInjector.instance.get(RequestService);
const reFetch = (sr: ServiceRequest) => requests().getOne<ServiceRequest>(sr.id, null);

/**
 * List / table Status click: open the whole request in a dialog, then return the refreshed request
 * (re-fetched when the dialog gives nothing back) so the caller can drop it into its row.
 */
export function openFullRequest(sr: ServiceRequest): Observable<ServiceRequest | null> {
    return dialog().open(ServiceRequestPageComponent, { data: { id: sr.id } }).afterClosed()
        .pipe(concatMap(updated => updated ? of(updated) : reFetch(sr)));
}

/** Action-status click: edit the request's actions in a dialog, return the refreshed request. */
export function openRequestActionGrid(sr: ServiceRequest): Observable<ServiceRequest | null> {
    return reFetch(sr).pipe(concatMap(full => {
        if (!full) { return of(null); }
        const field = ActionGridComponent.make('Request');
        field.makeControl();
        field.relatedFormGroup = new UntypedFormGroup({});
        field.setValue(full, false);
        return dialog().open(ActionListDialogComponent, {
            data: { control: field.control }, autoFocus: false, maxWidth: '95vw',
        }).afterClosed().pipe(concatMap(() => reFetch(sr)));
    }));
}

/** No formClick - on the request's own page the pill only shows the status. */
export function makeStatusPillField(): PillField {
    const field = FormPillComponent.make('Status', 'srStatus', {
        states: ServiceRequest.srStates,
        condensed: true,
        tableIconOnly: true,
        gridClick: (sr: AbstractObject) => openFullRequest(sr as ServiceRequest),
        tableClick: (sr: AbstractObject) => openFullRequest(sr as ServiceRequest),
        clickTip: () => $localize`Click to open the full request`,
    });
    field.cellOpts.heading = '';
    field.toolTip = $localize`Status`;
    return field;
}

export function makeActionStatusPillField(o: PillOpts = {}): PillField {
    const field = FormPillComponent.make('Action Status', 'subStatus', {
        states: ServiceRequest.srSubStates,
        condensed: true,
        tableIconOnly: true,
        afterFormChange: (sr: AbstractObject) => o.afterFormChange?.(sr as ServiceRequest),
        formClick: (sr: AbstractObject) => openRequestActionGrid(sr as ServiceRequest),
        gridClick: (sr: AbstractObject) => openRequestActionGrid(sr as ServiceRequest),
        tableClick: (sr: AbstractObject) => openRequestActionGrid(sr as ServiceRequest),
    });
    field.cellOpts.heading = '';
    field.toolTip = $localize`Action status`;
    return field;
}

/*
 * The assistant's part in a request. Client only - the page sets aiStatus / aiMessage / aiLog from
 * RequestService.getAiProposal (see aiStatusOf) and the pill never goes to the server.
 */
const aiStates: PillState[] = [
    { id: 'working', name: $localize`Working`, style: 'outline', color: 'amber', icon: 'smart_toy',
        working: true, tip: $localize`Asking the assistant about this request` },
    { id: 'review', name: $localize`To Review`, style: 'infill', color: 'amber', icon: 'smart_toy' },
    { id: AiLog.OUTCOME_AUTO, name: $localize`Auto Sent`, style: 'outline', color: 'green' },
    { id: AiLog.OUTCOME_ACCEPTED, name: $localize`Accepted`, style: 'outline', color: 'green' },
    { id: AiLog.OUTCOME_CHANGED, name: $localize`Changed`, style: 'outline', color: 'blue' },
    { id: AiLog.OUTCOME_REJECTED, name: $localize`Rejected`, style: 'outline', color: 'grey' },
    { id: 'failed', name: $localize`Failed`, style: 'infill', color: 'red' },
    { id: 'na', name: $localize`n/a` },
];

export function aiStatusOf(result: AiProposalResult | null): string {
    const log = result?.log;
    if (log?.failed || result?.reason === 'failed' || result?.reason === 'error') {
        return 'failed';
    }
    if (!log?.outcome) {
        return 'na';
    }
    return log.outcome === AiLog.OUTCOME_PENDING ? 'review' : log.outcome;
}

/** A reviewed answer says how well it survived review; otherwise what became of it, or the server's reason. */
function aiRowTip(sr: ServiceRequest): string {
    const log = sr.aiLog;
    if (!log || log.failed) {
        return sr.aiMessage ?? '';
    }
    return AiLog.reviewSummary(log) || (AI_OUTCOMES.find(o => o.id === log.outcome)?.name ?? '');
}

function aiClickTip(sr: ServiceRequest): string {
    if (sr.aiStatus === 'review') {
        return $localize`Click to review the answer`;
    }
    return sr.aiLog?.id ? $localize`Click to see the AI log` : '';
}

function openAiLog(sr: ServiceRequest): Observable<null> {
    if (sr.aiLog?.id) {
        MyInjector.instance.get(Router).navigateByUrl(AiLogPageNavRoute.getIdUrl(sr.aiLog.id));
    }
    return of(null);
}

interface AssistantPillOpts extends PillOpts {
    /** Open the pending answer for review - the page owns the dialog and what follows it. */
    review: (sr: ServiceRequest) => Observable<ServiceRequest | null>;
}

export function makeAssistantPillField(o: AssistantPillOpts): PillField {
    return FormPillComponent.make('Assistant', 'aiStatus', {
        states: aiStates,
        condensed: true,
        afterFormChange: (sr: AbstractObject) => o.afterFormChange?.(sr as ServiceRequest),
        formClick: (sr: AbstractObject) => (sr as ServiceRequest).aiStatus === 'review'
            ? o.review(sr as ServiceRequest)
            : openAiLog(sr as ServiceRequest),
        clickTip: (sr: AbstractObject) => aiClickTip(sr as ServiceRequest),
        rowTip: (sr: AbstractObject) => aiRowTip(sr as ServiceRequest),
    }).override({ visible: Field.formOnly });
}
